import { SortableContext, horizontalListSortingStrategy } from '@dnd-kit/sortable';
import cn from 'classnames';

import TCell from './TCell/TCell';

import { TRowProps } from './TRow.props';
import { useReactTableController } from '../../hooks/useReactTableController';

import styles from './TRow.module.css';

export default function TRow<T>({
  tableId,
  row,
  columnOrder,
  getRowProps,
  getCellProps,
  onCellClickHandler,
  onRowClick,
  onRowDoubleClick,
  onCellClick,
  rowHeight = 1,
}: TRowProps<T>) {
  // Подписываемся ТОЛЬКО на activeRowId и activeCellId (оптимизация)
  const activeRowId = useReactTableController(tableId, s => s.activeRowId);
  const activeCellId = useReactTableController(tableId, s => s.activeCellId);

  const handleRowClick = (e: React.MouseEvent) => {
    // Don't trigger row click if clicking on a checkbox or other interactive element
    const target = e.target as HTMLElement;

    // Check for selection column elements
    const isSelectionColumn =
      target.closest('[data-selection-column]') || target.closest('input[type="checkbox"]');

    // Check for other interactive elements
    const isInteractiveElement =
      target.closest('button') || target.closest('a') || target.closest('[role="button"]');

    if (isSelectionColumn || isInteractiveElement) {
      // Don't prevent default for selection column - let it handle its own events
      return;
    }

    if (onRowClick) {
      onRowClick(row, row.original);
    }
  };

  const handleRowDoubleClick = (e: React.MouseEvent) => {
    // Same validation as single click
    const target = e.target as HTMLElement;

    // Check for selection column elements
    const isSelectionColumn =
      target.closest('[data-selection-column]') || target.closest('input[type="checkbox"]');

    // Check for other interactive elements
    const isInteractiveElement =
      target.closest('button') || target.closest('a') || target.closest('[role="button"]');

    if (isSelectionColumn || isInteractiveElement) {
      // Don't prevent default for selection column - let it handle its own events
      return;
    }

    if (onRowDoubleClick) {
      onRowDoubleClick(row, row.original);
    }
  };

  const rowProps = getRowProps(row);
  const rowBgColor = rowProps?.style?.backgroundColor;

  return (
    <div
      {...{
        ...rowProps,
        className: cn(
          styles.row,
          {
            [styles.active]: row.id === activeRowId,
          },
          rowProps?.className || ''
        ),
        onClick: handleRowClick,
        onDoubleClick: handleRowDoubleClick,
        style: {
          cursor: onRowClick || onRowDoubleClick ? 'pointer' : 'default',
          ...rowProps?.style,
          ...(rowBgColor ? ({ '--row-bg': rowBgColor } as React.CSSProperties) : {}),
        },
      }}
    >
      {row.getVisibleCells().map((cell, index, cells) => {
        const isServiceColumn = (id: string) => id === 'select' || id === 'expander';
        const isFirstDataColumn =
          !isServiceColumn(cell.column.id) &&
          (index === 0 || isServiceColumn(cells[index - 1].column.id));
        return (
          <SortableContext
            key={cell.id}
            items={columnOrder}
            strategy={horizontalListSortingStrategy}
          >
            <TCell
              key={cell.id}
              cell={cell}
              getCellProps={getCellProps}
              onCellClickHandler={onCellClickHandler}
              isActive={row.id === activeRowId && cell.column.id === activeCellId}
              onCellClick={onCellClick}
              rowHeight={rowHeight}
              isFirstDataColumn={isFirstDataColumn}
            />
          </SortableContext>
        );
      })}
    </div>
  );
}
